<script setup>
import { Head, Link } from '@inertiajs/vue3'
</script>

<template>
  <div class="min-h-screen bg-white">
    <Head title="KVKK Aydınlatma Metni — INSATEQ">
      <meta name="description" content="INSATEQ kişisel verilerin korunması ve işlenmesi politikası. 6698 sayılı KVKK kapsamında aydınlatma metni, veri sahibi hakları ve başvuru prosedürleri." />
      <meta name="keywords" content="KVKK, kişisel verilerin korunması, aydınlatma metni, veri güvenliği, gizlilik, 6698 sayılı kanun" />
      <meta name="author" content="INSATEQ" />
      <meta name="robots" content="index, follow" />

      <!-- Open Graph -->
      <meta property="og:title" content="KVKK Aydınlatma Metni — INSATEQ" />
      <meta property="og:description" content="INSATEQ kişisel verilerin korunması politikası. 6698 sayılı KVKK kapsamında aydınlatma metni." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://insateq.com/kvkk" />
      <meta property="og:image" content="/icon-512.png" />
    </Head>

    <!-- Minimal Header -->
    <header class="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50">
      <div class="container mx-auto px-6 lg:px-12">
        <div class="flex justify-between items-center h-20">
          <Link :href="route('home')" class="flex items-center space-x-2">
            <div class="w-8 h-8 bg-blue-600 rounded flex items-center justify-center">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <span class="text-xl font-semibold text-gray-900">INSATEQ</span>
          </Link>

          <nav class="flex items-center space-x-6">
            <Link :href="route('home')" class="text-sm text-gray-600 hover:text-gray-900 transition">
              Ana Sayfa
            </Link>
            <Link :href="route('pricing')" class="text-sm text-gray-600 hover:text-gray-900 transition">
              Fiyatlandırma
            </Link>
            <Link :href="route('login')" class="text-sm text-blue-600 hover:text-blue-700 transition font-medium">
              Giriş Yap
            </Link>
          </nav>
        </div>
      </div>
    </header>

    <!-- Content -->
    <div class="container mx-auto px-6 lg:px-12 py-16 lg:py-20 max-w-4xl">
      <!-- Header -->
      <div class="mb-12">
        <h1 class="text-4xl lg:text-5xl font-light text-gray-900 mb-4">
          KVKK Aydınlatma Metni
        </h1>
        <p class="text-lg text-gray-600 font-light">
          Kişisel Verilerin Korunması Kanunu Kapsamında Bilgilendirme
        </p>
        <p class="text-sm text-gray-500 mt-2">
          Son güncelleme: {{ new Date().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' }) }}
        </p>
      </div>

      <!-- Content Sections -->
      <div class="prose prose-lg max-w-none">
        <!-- Section 1 -->
        <section class="mb-12">
          <h2 class="text-2xl font-medium text-gray-900 mb-4">1. Veri Sorumlusu</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") uyarınca, kişisel verileriniz;
            veri sorumlusu olarak INSATEQ ("Şirket") tarafından aşağıda açıklanan kapsamda
            işlenebilecektir.
          </p>
          <div class="bg-gray-50 rounded-lg p-6 space-y-2">
            <p class="text-gray-700"><strong>Şirket Ünvanı:</strong> [Şirket Ünvanı]</p>
            <p class="text-gray-700"><strong>Mersis No:</strong> [Mersis Numarası]</p>
            <p class="text-gray-700"><strong>Adres:</strong> [Şirket Adresi]</p>
            <p class="text-gray-700"><strong>E-posta:</strong> kvkk@insateq.com</p>
            <p class="text-gray-700"><strong>Telefon:</strong> [İletişim Numarası]</p>
          </div>
        </section>

        <!-- Section 2 -->
        <section class="mb-12">
          <h2 class="text-2xl font-medium text-gray-900 mb-4">2. İşlenen Kişisel Veriler</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            Hizmetlerimizi kullanırken aşağıdaki kategorilerdeki kişisel verileriniz işlenmektedir:
          </p>

          <div class="space-y-6">
            <div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Kimlik Bilgileri</h3>
              <p class="text-gray-700">Ad, soyad, T.C. kimlik numarası, doğum tarihi, medeni hali</p>
            </div>

            <div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">İletişim Bilgileri</h3>
              <p class="text-gray-700">E-posta adresi, telefon numarası, iş adresi, ikametgah adresi</p>
            </div>

            <div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Hesap ve Güvenlik Bilgileri</h3>
              <p class="text-gray-700">Kullanıcı adı, şifre (hash'lenmiş), güvenlik soruları, oturum bilgileri</p>
            </div>

            <div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">İşlem Güvenliği Bilgileri</h3>
              <p class="text-gray-700">IP adresi, çerez kayıtları, tarayıcı bilgileri, cihaz bilgileri, log kayıtları</p>
            </div>

            <div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Çalışan ve Personel Bilgileri</h3>
              <p class="text-gray-700">Pozisyon, departman, şirket adı, yönetici bilgileri, çalışma saatleri</p>
            </div>

            <div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Finansal Bilgiler</h3>
              <p class="text-gray-700">Fatura bilgileri, ödeme kayıtları, banka hesap bilgileri</p>
            </div>

            <div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Görsel ve İşitsel Kayıtlar</h3>
              <p class="text-gray-700">Profil fotoğrafı, belge görüntüleri</p>
            </div>

            <div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Lokasyon Bilgileri</h3>
              <p class="text-gray-700">GPS konumu, proje lokasyonu (mobil uygulama kullanımında)</p>
            </div>
          </div>
        </section>

        <!-- Section 3 -->
        <section class="mb-12">
          <h2 class="text-2xl font-medium text-gray-900 mb-4">3. Kişisel Verilerin İşlenme Amacı</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            Kişisel verileriniz aşağıdaki amaçlarla işlenmektedir:
          </p>

          <ul class="list-disc pl-6 space-y-3 text-gray-700">
            <li><strong>Hizmet Sunumu:</strong> İnşaat proje yönetimi platformunun işletilmesi, kullanıcı hesaplarının yönetimi</li>
            <li><strong>Kimlik Doğrulama:</strong> Kullanıcı kimlik doğrulama ve yetkilendirme işlemleri</li>
            <li><strong>İş Süreçleri:</strong> Personel takibi, zaman yönetimi, proje yönetimi hizmetlerinin sağlanması</li>
            <li><strong>Sözleşme İşlemleri:</strong> Abonelik sözleşmelerinin kurulması ve ifası</li>
            <li><strong>Finansal İşlemler:</strong> Faturalama, tahsilat ve ödeme işlemlerinin gerçekleştirilmesi</li>
            <li><strong>Müşteri İlişkileri:</strong> Müşteri destek hizmetlerinin sunulması, talep ve şikayetlerin yönetimi</li>
            <li><strong>Güvenlik:</strong> Platform güvenliğinin sağlanması, yetkisiz erişimin önlenmesi</li>
            <li><strong>İletişim:</strong> Bilgilendirme, duyuru ve pazarlama faaliyetlerinin yürütülmesi</li>
            <li><strong>Analiz ve Raporlama:</strong> Kullanım istatistikleri, performans analizi, hizmet iyileştirme</li>
            <li><strong>Yasal Yükümlülükler:</strong> Kanunlarda öngörülen saklama ve raporlama yükümlülüklerinin yerine getirilmesi</li>
          </ul>
        </section>

        <!-- Section 4 -->
        <section class="mb-12">
          <h2 class="text-2xl font-medium text-gray-900 mb-4">4. Kişisel Verilerin Aktarılması</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            Kişisel verileriniz, KVKK'nın 8. ve 9. maddelerinde belirtilen şartlar çerçevesinde
            aşağıdaki taraflara aktarılabilir:
          </p>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">Yurt İçi Aktarımlar:</h3>
          <ul class="list-disc pl-6 space-y-2 text-gray-700 mb-6">
            <li>İş ortaklarımız (hizmet sağlayıcılar)</li>
            <li>Ödeme kuruluşları ve bankalar</li>
            <li>Bulut hizmet sağlayıcıları</li>
            <li>Hosting ve altyapı hizmet sağlayıcıları</li>
            <li>E-posta ve SMS hizmet sağlayıcıları</li>
            <li>Yetkili kamu kurum ve kuruluşları</li>
            <li>Denetim ve danışmanlık şirketleri</li>
          </ul>

          <h3 class="text-xl font-medium text-gray-900 mb-3">Yurt Dışı Aktarımlar:</h3>
          <p class="text-gray-700 leading-relaxed mb-4">
            Kişisel verileriniz, yeterli korumaya sahip olan veya yeterli korumanın bulunmadığı ülkeler için
            Kurul tarafından belirlenen standart sözleşmeler kullanılmak suretiyle yurt dışına aktarılabilir.
          </p>
          <ul class="list-disc pl-6 space-y-2 text-gray-700">
            <li>AWS, Google Cloud Platform, Microsoft Azure (sunucu hizmetleri)</li>
            <li>Sendgrid, Mailgun (e-posta servisleri)</li>
            <li>Stripe, PayPal (ödeme altyapısı)</li>
          </ul>
        </section>

        <!-- Section 5 -->
        <section class="mb-12">
          <h2 class="text-2xl font-medium text-gray-900 mb-4">5. Kişisel Verilerin Toplanma Yöntemi</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            Kişisel verileriniz aşağıdaki yöntemlerle toplanmaktadır:
          </p>
          <ul class="list-disc pl-6 space-y-2 text-gray-700">
            <li><strong>Web sitesi ve mobil uygulama:</strong> Hesap oluşturma, form doldurma, platform kullanımı</li>
            <li><strong>Otomatik sistemler:</strong> Çerezler, log kayıtları, analiz araçları</li>
            <li><strong>E-posta ve telefon:</strong> Müşteri hizmetleri iletişimi</li>
            <li><strong>Üçüncü taraflar:</strong> Ödeme hizmet sağlayıcıları, API entegrasyonları</li>
            <li><strong>Fiziksel ortam:</strong> Sözleşmeler, formlar (gerektiğinde)</li>
          </ul>
        </section>

        <!-- Section 6 -->
        <section class="mb-12">
          <h2 class="text-2xl font-medium text-gray-900 mb-4">6. İlgili Kişinin KVKK Kapsamındaki Hakları</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            KVKK'nın 11. maddesi uyarınca, ilgili kişi olarak aşağıdaki haklara sahipsiniz:
          </p>

          <ul class="list-disc pl-6 space-y-3 text-gray-700 mb-6">
            <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme</li>
            <li>Kişisel verileriniz işlenmişse buna ilişkin bilgi talep etme</li>
            <li>Kişisel verilerinizin işlenme amacını ve bunların amacına uygun kullanılıp kullanılmadığını öğrenme</li>
            <li>Yurt içinde veya yurt dışında kişisel verilerinizin aktarıldığı üçüncü kişileri bilme</li>
            <li>Kişisel verilerinizin eksik veya yanlış işlenmiş olması halinde bunların düzeltilmesini isteme</li>
            <li>KVKK'nın 7. maddesinde öngörülen şartlar çerçevesinde kişisel verilerinizin silinmesini veya yok edilmesini isteme</li>
            <li>Düzeltme ve silme işlemlerinin, kişisel verilerin aktarıldığı üçüncü kişilere bildirilmesini isteme</li>
            <li>İşlenen verilerin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle aleyhinize bir sonucun ortaya çıkmasına itiraz etme</li>
            <li>Kişisel verilerinizin kanuna aykırı olarak işlenmesi sebebiyle zarara uğramanız halinde zararın giderilmesini talep etme</li>
          </ul>

          <div class="bg-blue-50 border-l-4 border-blue-600 p-6 mb-6">
            <p class="text-blue-900 font-medium mb-2">Başvuru Yöntemi:</p>
            <p class="text-blue-800 text-sm leading-relaxed">
              Yukarıda belirtilen haklarınızı kullanmak için kimliğinizi tespit edici gerekli bilgiler
              ile birlikte talebinizi kvkk@insateq.com adresine veya [Şirket Adresi] adresine
              yazılı olarak iletebilirsiniz.
            </p>
          </div>

          <p class="text-gray-700 leading-relaxed">
            Başvurularınız, talebin niteliğine göre en geç 30 gün içinde ücretsiz olarak
            sonuçlandırılacaktır. Ancak, işlemin ayrıca bir maliyet gerektirmesi halinde,
            Kişisel Verileri Koruma Kurulu tarafından belirlenen tarifedeki ücret alınabilir.
          </p>
        </section>

        <!-- Section 7 -->
        <section class="mb-12">
          <h2 class="text-2xl font-medium text-gray-900 mb-4">7. Kişisel Verilerin Saklanması ve İmhası</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            Kişisel verileriniz, işlenme amaçlarının gerektirdiği süre boyunca saklanır:
          </p>

          <ul class="list-disc pl-6 space-y-2 text-gray-700 mb-4">
            <li><strong>Aktif kullanıcı verileri:</strong> Hesap aktif olduğu sürece</li>
            <li><strong>Finansal kayıtlar:</strong> Vergi Usul Kanunu uyarınca 10 yıl</li>
            <li><strong>İletişim kayıtları:</strong> Sorunun çözüldüğü tarihten itibaren 2 yıl</li>
            <li><strong>Log kayıtları:</strong> 2 yıl</li>
            <li><strong>Pazarlama verileri:</strong> Açık rızanın geri alınmasına kadar</li>
          </ul>

          <p class="text-gray-700 leading-relaxed mb-4">
            Saklama sürelerinin dolması veya işleme şartlarının ortadan kalkması durumunda,
            kişisel verileriniz KVKK ve ilgili mevzuat hükümlerine uygun olarak:
          </p>

          <ul class="list-disc pl-6 space-y-2 text-gray-700">
            <li>Silinir</li>
            <li>Yok edilir</li>
            <li>Anonim hale getirilir</li>
          </ul>
        </section>

        <!-- Section 8 -->
        <section class="mb-12">
          <h2 class="text-2xl font-medium text-gray-900 mb-4">8. Güvenlik Önlemleri</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            Kişisel verilerinizin güvenliğini sağlamak için aşağıdaki teknik ve idari tedbirler alınmıştır:
          </p>

          <div class="space-y-4">
            <div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Teknik Önlemler:</h3>
              <ul class="list-disc pl-6 space-y-2 text-gray-700">
                <li>SSL/TLS şifreleme</li>
                <li>Güvenlik duvarları</li>
                <li>Sızma testleri</li>
                <li>Güvenlik yamalarının düzenli güncellemesi</li>
                <li>Veri yedekleme sistemleri</li>
                <li>Şifreli veri saklama</li>
              </ul>
            </div>

            <div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">İdari Önlemler:</h3>
              <ul class="list-disc pl-6 space-y-2 text-gray-700">
                <li>Erişim yetkilendirme sistemi</li>
                <li>Personel gizlilik taahhütleri</li>
                <li>KVKK eğitimleri</li>
                <li>Düzenli güvenlik denetimleri</li>
                <li>Veri işleme envanteri</li>
              </ul>
            </div>
          </div>
        </section>

        <!-- Section 9 -->
        <section class="mb-12">
          <h2 class="text-2xl font-medium text-gray-900 mb-4">9. İletişim ve Başvuru</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            KVKK kapsamındaki haklarınızı kullanmak veya sorularınız için:
          </p>

          <div class="bg-gray-50 rounded-lg p-6 space-y-4">
            <div>
              <p class="text-gray-700 font-medium mb-1">E-posta Başvurusu:</p>
              <p class="text-gray-700">kvkk@insateq.com</p>
              <p class="text-sm text-gray-600 mt-1">
                Kimliğinizi tespit edici belgelerle birlikte başvurunuzu yapabilirsiniz.
              </p>
            </div>

            <div>
              <p class="text-gray-700 font-medium mb-1">Posta Başvurusu:</p>
              <p class="text-gray-700">[Şirket Adresi]</p>
              <p class="text-sm text-gray-600 mt-1">
                İmzalı dilekçeniz ve kimlik fotokopisi ile başvurabilirsiniz.
              </p>
            </div>

            <div>
              <p class="text-gray-700 font-medium mb-1">Telefon:</p>
              <p class="text-gray-700">[İletişim Numarası]</p>
              <p class="text-sm text-gray-600 mt-1">
                Hafta içi 09:00 - 18:00 saatleri arasında
              </p>
            </div>
          </div>

          <div class="mt-6 bg-yellow-50 border-l-4 border-yellow-600 p-4">
            <p class="text-yellow-900 text-sm">
              <strong>Önemli:</strong> Başvurularınızın değerlendirilebilmesi için kimliğinizi
              tespit edici bilgi ve belgelerin eksiksiz ve doğru olması gerekmektedir.
            </p>
          </div>
        </section>

        <!-- Section 10 -->
        <section class="mb-12">
          <h2 class="text-2xl font-medium text-gray-900 mb-4">10. Aydınlatma Metninin Güncellemesi</h2>
          <p class="text-gray-700 leading-relaxed">
            Bu aydınlatma metni, KVKK ve ilgili mevzuattaki değişiklikler veya Şirket'in
            veri işleme faaliyetlerindeki değişiklikler nedeniyle güncellenebilir.
            Güncellemeler bu sayfada yayınlanacak ve önemli değişiklikler size bildirilecektir.
          </p>
        </section>
      </div>
    </div>

    <!-- Footer -->
    <footer class="py-12 bg-gray-50 border-t border-gray-100 mt-16">
      <div class="container mx-auto px-6 lg:px-12">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          <!-- Company Info -->
          <div class="col-span-1">
            <div class="flex items-center space-x-2 mb-4">
              <div class="w-8 h-8 bg-blue-600 rounded flex items-center justify-center">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <span class="text-xl font-semibold text-gray-900">INSATEQ</span>
            </div>
            <p class="text-sm text-gray-600">
              Modern inşaat firmaları için dijital yönetim platformu
            </p>
          </div>

          <!-- Links -->
          <div>
            <h3 class="font-semibold text-gray-900 mb-3">Ürün</h3>
            <ul class="space-y-2 text-sm text-gray-600">
              <li><Link :href="route('home')" class="hover:text-gray-900 transition">Özellikler</Link></li>
              <li><Link :href="route('pricing')" class="hover:text-gray-900 transition">Fiyatlandırma</Link></li>
            </ul>
          </div>

          <div>
            <h3 class="font-semibold text-gray-900 mb-3">Hesap</h3>
            <ul class="space-y-2 text-sm text-gray-600">
              <li><Link :href="route('login')" class="hover:text-gray-900 transition">Giriş Yap</Link></li>
              <li><Link :href="route('register')" class="hover:text-gray-900 transition">Hesap Oluştur</Link></li>
            </ul>
          </div>

          <div>
            <h3 class="font-semibold text-gray-900 mb-3">Yasal</h3>
            <ul class="space-y-2 text-sm text-gray-600">
              <li><Link :href="route('privacy')" class="hover:text-gray-900 transition">Gizlilik Politikası</Link></li>
              <li><Link :href="route('terms')" class="hover:text-gray-900 transition">Kullanım Şartları</Link></li>
              <li><Link :href="route('kvkk')" class="hover:text-gray-900 transition font-medium">KVKK</Link></li>
            </ul>
          </div>
        </div>

        <div class="pt-8 border-t border-gray-200">
          <p class="text-center text-sm text-gray-600">
            &copy; 2025 INSATEQ. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<style scoped>
.prose h2, .prose h3 {
  scroll-margin-top: 6rem;
}
</style>
